/**
 * Auth context for the admin SPA (admin-arch §7).
 *
 * The access token is held *in memory only* — never persisted to browser
 * storage (admin-arch §7.1, security review C-1 fix). Long-lived state lives
 * in the httpOnly refresh cookie set by the backend.
 *
 * On mount the provider performs a silent `POST /auth/refresh`: if the user has
 * a valid refresh cookie (typical case after page reload or new tab) they are
 * restored to an authenticated state without seeing the login screen. Otherwise
 * the bootstrap quietly resolves to "anonymous" and protected routes redirect
 * to `/login`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { bindAuth } from '@/lib/api-client';
import { clearTusUploadStorage } from '@/api/upload';

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
  // BUG-019: BE returns 'super_admin' as a first-class role (alongside the
  // `is_super_admin` flag). FE union was missing it, which made every
  // `user.role !== 'admin'` guard silently bounce super_admin to `/` —
  // also caused TS2367 dead-comparison errors on the BUG-013/015 fixes.
  role: 'admin' | 'super_admin' | 'selector' | 'client_owner' | 'client_manager';
  /**
   * Super-admin flag — separate from `role` because the user is still an
   * `admin` but holds elevated rights (create tag categories, hard-delete
   * private tags of other owners). Optional for backwards compatibility with
   * sessions issued before the flag was added.
   */
  is_super_admin?: boolean;
}

interface AuthContextValue {
  accessToken: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  isBootstrapping: boolean;
  setAccessToken: (token: string | null) => void;
  setUser: (user: AuthUser | null) => void;
  /** Local-only logout: clears in-memory state and React Query cache. Callers
   * that need to invalidate the server-side session should call the
   * `/auth/logout` endpoint first via `useLogout()`. */
  clearSession: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface RefreshSuccess {
  access_token: string;
  user?: AuthUser;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [accessToken, setAccessTokenState] = useState<string | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const queryClient = useQueryClient();

  // What the `bindAuth` getter reads. It is written where the token changes, never during a render: the repeat after a
  // refresh runs before the next render, so a ref that waited for it would send the token the server has just refused
  // (BUG-393), and a render in another lane would put the old token back.
  const tokenRef = useRef<string | null>(null);

  const writeToken = useCallback((token: string | null) => {
    tokenRef.current = token;
    setAccessTokenState(token);
  }, []);

  const setAccessToken = useCallback(
    (token: string | null) => {
      writeToken(token);
      if (token === null) {
        setUser(null);
      }
    },
    [writeToken],
  );

  const clearSession = useCallback(() => {
    writeToken(null);
    setUser(null);
    queryClient.clear();
    // Wave 2.66 FE (TASK-051a-followup): tus URL fingerprints are now persisted
    // for resume-after-refresh. Wipe them on logout so a fresh sign-in does
    // not inherit pending uploads from the previous user.
    clearTusUploadStorage();
  }, [queryClient, writeToken]);

  // Wire api-client mutex to live token state. Bind once.
  useEffect(() => {
    bindAuth(
      () => tokenRef.current,
      (token) => setAccessToken(token),
    );
  }, [setAccessToken]);

  // Silent-refresh bootstrap (C-1 fix). Runs once on mount.
  // BUG-011 fix: /auth/refresh response does NOT include user payload, so
  // after a successful refresh we additionally fetch /auth/me to populate
  // the chrome (Topbar, Sidebar) + unblock pages that early-return on a
  // null `user` (AccountsPage / VenueCardPage / etc.). Without this, after
  // any page reload all protected routes render empty <main>.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '';
        const resp = await fetch(`${baseUrl}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        if (cancelled) return;
        if (resp.ok) {
          const data = (await resp.json()) as RefreshSuccess;
          if (data?.access_token) {
            writeToken(data.access_token);
            if (data.user) {
              setUser(data.user);
            } else {
              // Fallback: refresh response is access-token-only — fetch profile.
              try {
                const meResp = await fetch(`${baseUrl}/auth/me`, {
                  headers: { Authorization: `Bearer ${data.access_token}` },
                });
                if (cancelled) return;
                if (meResp.ok) {
                  const meData = (await meResp.json()) as AuthUser;
                  setUser(meData);
                }
              } catch {
                // /auth/me failed — leave user null; pages will redirect via
                // ProtectedRoute or render fallback chrome.
              }
            }
          }
        }
        // 401 / network error → stay anonymous; protected routes will redirect.
      } catch {
        // Same as 401 — silently anonymous.
      } finally {
        if (!cancelled) setIsBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [writeToken]);

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        user,
        isAuthenticated: !!accessToken,
        isBootstrapping,
        setAccessToken,
        setUser,
        clearSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
